import { gql, type TypedDocumentNode } from "@apollo/client";
import type { ParticipationHistoryData } from "./events/types";

// Re-export from features directory for backward compatibility
export { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";

export type MembershipAchievementCategoryEnum = "ACHIEVEMENT" | "AWARD" | "CERTIFICATION";

export type MembershipAchievementForMemberFilterEnum = MembershipAchievementCategoryEnum | "ALL";

export type MembershipAchievementCode = {
  code: string | null;
  description: string | null;
  title: string;
};

export const NATIVE_ACHIEVEMENT_CODE = "NATIVE_DO_NOT_USE";

export function displayAchievementCodeTitle(code: MembershipAchievementCode): string | null {
  return code.code === NATIVE_ACHIEVEMENT_CODE ? null : code.title;
}

export type MembershipRecognizingEntity = {
  id: string;
  legacyId: string | null;
  name: string;
};

export type MembershipAchievementForMember = {
  category: MembershipAchievementCategoryEnum;
  code: MembershipAchievementCode;
  createdDate: string | null;
  expiresOn: string | null;
  id: string;
  recognitionYear: string | null;
  recognizedAt: string | null;
  recognizingEntity: MembershipRecognizingEntity | null;
  remarks: string | null;
  title: string | null;
  typeId: string;
};

export type MembershipAchievementType = {
  category: MembershipAchievementCategoryEnum;
  ccmsAttributeType: string | null;
  ccmsCodeType: string | null;
  codes: MembershipAchievementCode[];
  description: string | null;
  id: string;
  native: boolean;
  title: string;
};

export function findAchievementTypeForMember(
  achievementTypes: MembershipAchievementType[],
  achievement: MembershipAchievementForMember
) {
  const byTypeId = achievementTypes.find((achievementType) => achievementType.id === achievement.typeId);
  if (byTypeId) return byTypeId;

  const code = achievement.code.code?.trim();
  if (!code) return undefined;

  const byCode = achievementTypes.filter((achievementType) =>
    achievementType.codes.some((item) => item.code?.trim() === code)
  );

  return byCode.length === 1 ? byCode[0] : undefined;
}

export type AchievementsForMemberQueryResponse = {
  achievementsForMember: MembershipAchievementForMember[];
};

export type AchievementsForMemberQueryVariables = {
  achievement?: MembershipAchievementForMemberFilterEnum;
  pgaId: string;
};

export type AchievementTypesQueryResponse = {
  achievementTypes: MembershipAchievementType[];
};

export type AchievementTypesQueryVariables = {
  category?: MembershipAchievementCategoryEnum;
  search?: string | null;
};

export const ACHIEVEMENTS_FOR_MEMBER_QUERY: TypedDocumentNode<
  AchievementsForMemberQueryResponse,
  AchievementsForMemberQueryVariables
> = gql`
  query AchievementsForMember($pgaId: String!, $achievement: MembershipAchievementForMemberFilterEnum) {
    achievementsForMember(pgaId: $pgaId, achievement: $achievement) {
      id
      typeId
      category
      title
      createdDate
      expiresOn
      recognitionYear
      recognizedAt
      remarks
      recognizingEntity {
        id
        name
        legacyId
      }
      code {
        code
        title
        description
      }
    }
  }
`;

export const ACHIEVEMENT_TYPES_QUERY: TypedDocumentNode<AchievementTypesQueryResponse, AchievementTypesQueryVariables> =
  gql`
    query AchievementTypes($category: MembershipAchievementCategoryEnum, $search: String) {
      achievementTypes(category: $category, search: $search) {
        id
        category
        title
        description
        native
        ccmsCodeType
        ccmsAttributeType
        codes {
          code
          title
          description
        }
      }
    }
  `;

export const EVENT_REGISTRATION_HISTORY_QUERY = gql`
  query eventRegistrationHistory($pgaId: String!) {
    eventRegistrationHistory(pgaId: $pgaId) {
      id
      acceptedTerms
      canceledAt
      registeredAt
      order {
        id
        totalPaid
        totalRefunded
        status
        paymentStatus
        balance
      }
      event {
        id
        title
        slug
        status
        cancelledAt
        eventType
        activityType
        startsAt
        endsAt
        facility {
          id
          name
        }
        location
        locationType
        timezone
      }
      sessions {
        id
        duration
        endsAt
        hasAvailability
        startsAt
        title
      }
    }
  }
`;

type ParticipationHistoryQueryVariables = { pgaId: string };

export const PARTICIPATION_HISTORY_QUERY: TypedDocumentNode<
  ParticipationHistoryData,
  ParticipationHistoryQueryVariables
> = gql`
  query ParticipationHistory($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      historicalTournamentParticipations {
        eventId
        programName
        eventName
        eventStartDate
        eventEndDate
        pgaId
        registrationName
        registrationType
        registrationDate
        vipFlag
        registrationStatus
        registrationSerialNumber
      }
    }
  }
`;
